import { z } from 'zod';
/**
 * The spreadsheet's header row, in this exact order. Both the upload parser
 * (validating a submitted header against these names) and the downloadable
 * template (S30-TASK-014's `buildEmployeeImportTemplateCsv`) read from this
 * one array rather than each hardcoding the column names, so the two can
 * never drift apart.
 */
export const EMPLOYEE_IMPORT_REQUIRED_COLUMNS = ['First Name', 'Last Name', 'Work Email'];
export const EMPLOYEE_IMPORT_OPTIONAL_COLUMNS = [
    'Mobile Phone',
    'Manager Email',
    'Assistant Emails',
    'Primary Location',
    'Secondary Locations',
];
export const EMPLOYEE_IMPORT_COLUMNS = [
    ...EMPLOYEE_IMPORT_REQUIRED_COLUMNS,
    ...EMPLOYEE_IMPORT_OPTIONAL_COLUMNS,
];
/**
 * Directory-only optional columns (adr/0036 decision 7; S47-rsq5 Constraint 11). ALWAYS accepted
 * by the parser and STORED on every tenant (Person.title / departmentId already exist and are
 * editable in Platform Admin's People page, so storing reveals nothing new, and a parser whose
 * outcome does not depend on tenant state keeps preview and commit identical). OFFERED — in the
 * template download and the admin person form — only when `directory.access` is on.
 * Pronouns and Bio (Phase 5) are self-editable: a blank cell never clears them (adr/0036 addendum
 * 'Profile fields and the import').
 */
export const EMPLOYEE_IMPORT_DIRECTORY_COLUMNS = ['Title', 'Department', 'Pronouns', 'Bio'];
export const employeeImportDirectoryColumnSchema = z.enum(EMPLOYEE_IMPORT_DIRECTORY_COLUMNS);
/** Every header the parser accepts, in template order. `EMPLOYEE_IMPORT_COLUMNS` stays the
 *  Visitor-only template (unchanged, 8); the Directory-enabled template is this (10). */
export const EMPLOYEE_IMPORT_ACCEPTED_COLUMNS = [
    ...EMPLOYEE_IMPORT_COLUMNS,
    ...EMPLOYEE_IMPORT_DIRECTORY_COLUMNS,
];
/** Longest Title or Department cell — equal to `personInputSchema.title`'s and `createDepartmentSchema.name`'s 120. */
export const EMPLOYEE_IMPORT_DIRECTORY_TEXT_MAX = 120;
/**
 * `GET /api/web/people/field-set` — which optional field groups the person form and the import
 * template offer for the caller's tenant (S47-rsq5 Constraint 11). `directoryFields` = the tenant
 * has `directory.access`: the template offers Title and Department, the form offers Department (Job
 * title is an existing form field shown to every tenant; Phase 5: Pronouns, Bio). Presentation only;
 * the parser and the person routes accept those fields on every tenant.
 */
export const employeeFieldSetDTOSchema = z.object({ directoryFields: z.boolean() }).strict();
/**
 * Upload constraints, mirroring `kiosk-branding.ts`'s `KIOSK_LOGO_*` trio: a
 * schema the route can `safeParse` the declared `content-type` header
 * against, and an exact byte cap to compare `body.length` to. The legacy
 * binary `.xls` type (`application/vnd.ms-excel`) is deliberately
 * unsupported — only CSV and modern `.xlsx`.
 */
export const EMPLOYEE_IMPORT_CONTENT_TYPES = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const employeeImportContentTypeSchema = z.enum(EMPLOYEE_IMPORT_CONTENT_TYPES);
/** Exact byte cap compared against `body.length` in the route — precise, not the parser's
 *  coarser limit (docs/ab-standards-digest S10; mirrors `KIOSK_LOGO_MAX_BYTES`). */
export const EMPLOYEE_IMPORT_MAX_BYTES = 2 * 1024 * 1024;
/** Data rows only, header excluded — bounds the commit transaction and its per-row audit rows. */
export const EMPLOYEE_IMPORT_MAX_ROWS = 1000;
/**
 * One parsed spreadsheet data row — the directory-sync seam a future
 * connector reuses (Constraint 15). Every field is the exact trimmed cell
 * value (or `null`/`[]` for an absent optional column): NOT yet validated as
 * an email, NOT yet resolved against any location or person. Validation and
 * resolution belong to `apps/api/src/lib/employee-import.ts` (S30-TASK-013),
 * which consumes this shape rather than redefining it.
 *
 * `row` counts the header as row 1, so the first data row is `2` — the same
 * number a person filling the file in a real spreadsheet application sees,
 * and what every error/notice below reports.
 */
export const importRowSchema = z.object({
    row: z.number().int().min(2),
    firstName: z.string(),
    lastName: z.string(),
    workEmail: z.string(),
    mobilePhone: z.string().nullable(),
    managerEmail: z.string().nullable(),
    assistantEmails: z.array(z.string()),
    primaryLocation: z.string().nullable(),
    secondaryLocations: z.array(z.string()),
    /** Trimmed cell; null for a blank cell or an absent column (absence is carried by the planner context). */
    title: z.string().nullable(),
    department: z.string().nullable(),
    /** Trimmed cell; null for a blank cell or an absent column. */
    pronouns: z.string().nullable(),
    bio: z.string().nullable(),
});
/**
 * Every row-level and header-level failure this milestone names. A flat,
 * closed union — never a hand-written interface — so a new failure mode is a
 * one-line addition here rather than a duplicated string somewhere else.
 * Travels inside a `200 { data: EmployeeImportReportDTO }` body (Constraint
 * 10 — a rejected preview/commit is `outcome: 'INVALID'`, not a 4xx), never
 * through the error middleware, so these codes deliberately do NOT belong to
 * the global `ERROR_CODES` union in `errors.ts`.
 */
export const EMPLOYEE_IMPORT_ERROR_CODES = [
    'UNKNOWN_COLUMN',
    'DUPLICATE_COLUMN',
    'MISSING_REQUIRED_COLUMN',
    'FIRST_NAME_REQUIRED',
    'LAST_NAME_REQUIRED',
    'WORK_EMAIL_REQUIRED',
    'WORK_EMAIL_INVALID',
    'MANAGER_EMAIL_INVALID',
    'ASSISTANT_EMAIL_INVALID',
    'DUPLICATE_WORK_EMAIL',
    'MANAGER_NOT_FOUND',
    'ASSISTANT_NOT_FOUND',
    'ASSISTANT_SELF',
    'MANAGER_CYCLE',
    'LOCATION_NOT_FOUND',
    'LOCATION_NAME_AMBIGUOUS',
    'NO_DEFAULT_LOCATION',
    // A Title, Department, Pronouns or Bio cell that is not plain text within its column's limit
    // (120, 120, 40, 500); params: { column, max }.
    'DIRECTORY_TEXT_INVALID',
    // Two or more of the tenant's departments carry that name; params: { name }.
    'DEPARTMENT_NAME_AMBIGUOUS',
];
export const employeeImportErrorCodeSchema = z.enum(EMPLOYEE_IMPORT_ERROR_CODES);
/**
 * Informational only — a notice never blocks a commit, unlike an error code
 * above. Kept as a separate, disjoint union so a client (and the i18n layer,
 * S30-TASK-016) can tell at the type level whether a code is "this blocks the
 * commit" or "this is FYI only" without inspecting which array it came from.
 */
export const EMPLOYEE_IMPORT_NOTICE_CODES = [
    'PRIMARY_LOCATION_DEFAULTED_TO_CONTEXT',
    'PRIMARY_LOCATION_DEFAULTED_TO_TENANT_FIRST',
    'PRIMARY_LOCATION_KEPT_EXISTING_CONFLICT_ADDED_SECONDARY',
    // The first row naming a department the tenant does not have — the commit creates it as a
    // top-level department; params: { name }.
    'DEPARTMENT_CREATED',
    // The row matched an employee the connected directory owns (S49-3u50): their synced fields (First Name, Last
    // Name, Work Email, Mobile Phone, Title, Department, Manager Email, Primary Location) were kept as stored and only
    // Pronouns, Bio, Assistant Emails and Secondary Locations were applied; params: {}.
    'SYNCED_FIELDS_KEPT',
];
export const employeeImportNoticeCodeSchema = z.enum(EMPLOYEE_IMPORT_NOTICE_CODES);
/**
 * `column` is `null` for a failure that cannot be pinned to one cell
 * (`DUPLICATE_WORK_EMAIL`, `MANAGER_CYCLE`) — otherwise it names the column
 * the message is about. A header-level failure (`UNKNOWN_COLUMN`,
 * `DUPLICATE_COLUMN`, `MISSING_REQUIRED_COLUMN`) always carries `row: 1` (the
 * header row itself). `params` carries whatever the translated string
 * interpolates — a location name, an email address the admin already typed
 * into the cell they are looking at — and nothing this report doesn't already
 * show the caller (docs/008 S12: this report is returned, never logged).
 */
export const employeeImportRowErrorSchema = z.object({
    row: z.number().int().min(1),
    column: z.string().nullable(),
    code: employeeImportErrorCodeSchema,
    params: z.record(z.string()),
});
export const employeeImportRowNoticeSchema = z.object({
    row: z.number().int().min(2),
    code: employeeImportNoticeCodeSchema,
    params: z.record(z.string()),
});
export const EMPLOYEE_IMPORT_OUTCOMES = ['VALID', 'INVALID', 'COMMITTED'];
/**
 * The report envelope both the preview and (Phase 4) commit routes answer
 * with. `errors`/`notices` are ALWAYS both present (empty arrays when there
 * is nothing to say) so a client never branches on `undefined`. `notices` and
 * the two counts are meaningful ONLY when `outcome !== 'INVALID'` — an
 * invalid file reports zero creates/updates/notices by construction
 * (Constraint 10: nothing is planned for a file that will not be written),
 * never a partial preview of the rows that happened to be clean.
 */
export const employeeImportReportSchema = z.object({
    outcome: z.enum(EMPLOYEE_IMPORT_OUTCOMES),
    totalRows: z.number().int().min(0),
    errors: z.array(employeeImportRowErrorSchema),
    notices: z.array(employeeImportRowNoticeSchema),
    toCreateCount: z.number().int().min(0),
    toUpdateCount: z.number().int().min(0),
    /** The Directory-only columns the file's header carried, in canonical order. ABSENT when it
     *  carried none, so every pre-existing report is unchanged. Tenant-independent (it describes the
     *  file); the console decides whether to say "saved, shown only in the Employee Directory". */
    directoryColumns: z.array(employeeImportDirectoryColumnSchema).min(1).optional(),
    /** `platform.max_people`: present ONLY when committing this file's creates would pass the plan's
     *  cap (`current + toCreateCount > limit`). `overBy` is how many creates do not fit. The import is
     *  all-or-nothing, so the commit refuses the whole file (402 `PLAN_LIMIT_REACHED`); the preview
     *  carries this so the console can warn before the admin clicks Import. Updates never count. */
    peopleLimit: z
        .object({
        limit: z.number().int().min(0),
        current: z.number().int().min(0),
        overBy: z.number().int().min(1),
    })
        .strict()
        .optional(),
});
//# sourceMappingURL=employee-import.js.map